import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Shield, Loader2, LogOut, Search, Smartphone, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSecurityPage = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/security/login-events?limit=200`, { withCredentials: true });
      setEvents(r.data.events || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load login events');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const forceLogout = async (userId, email) => {
    if (!window.confirm(`Log out ${email} from every device? Their active sessions will be revoked immediately.`)) return;
    setBusy(userId);
    try {
      await axios.post(`${API_URL}/api/admin/security/force-logout/${userId}`, {}, { withCredentials: true });
      toast.success(`${email} — all sessions revoked`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Force logout failed');
    } finally { setBusy(null); }
  };

  if (user?.role !== 'admin') {
    return <div className="p-8 text-center text-muted-foreground">Admin access required.</div>;
  }

  const filtered = q
    ? events.filter(e => (e.email || '').toLowerCase().includes(q.toLowerCase()) || (e.ip || '').includes(q))
    : events;

  // group by user to show one row per user with latest event + button
  const perUser = {};
  filtered.forEach(e => {
    if (!perUser[e.user_id]) perUser[e.user_id] = { user_id: e.user_id, email: e.email, latest: e, count: 0 };
    perUser[e.user_id].count += 1;
  });
  const userRows = Object.values(perUser).sort((a, b) => (b.latest.at || '').localeCompare(a.latest.at || ''));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-security-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="w-7 h-7 text-primary" /> Security — Login Activity
        </h1>
        <p className="text-muted-foreground">Recent successful sign-ins across all users. Force-logout revokes every JWT for that account instantly.</p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Filter by email or IP"
                value={q}
                onChange={e => setQ(e.target.value)}
                className="pl-9"
                data-testid="security-search"
              />
            </div>
            <Button variant="outline" size="icon" onClick={fetchEvents} disabled={loading} data-testid="security-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : userRows.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No login events yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table w-full">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Latest device</th>
                    <th>IP</th>
                    <th>Last login</th>
                    <th>Total sessions</th>
                    <th className="text-right">Action</th>
                  </tr>
                </thead>
                <tbody data-testid="security-users-tbody">
                  {userRows.map(r => (
                    <tr key={r.user_id} data-testid={`security-row-${r.user_id}`}>
                      <td className="font-medium">{r.email}</td>
                      <td className="flex items-center gap-2 text-sm">
                        <Smartphone className="w-3.5 h-3.5 text-muted-foreground" />
                        {r.latest.device}
                      </td>
                      <td className="font-mono text-xs">{r.latest.ip}</td>
                      <td className="text-xs">{r.latest.at ? new Date(r.latest.at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : ''}</td>
                      <td><Badge variant="outline">{r.count}</Badge></td>
                      <td className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          disabled={busy === r.user_id}
                          onClick={() => forceLogout(r.user_id, r.email)}
                          data-testid={`force-logout-${r.user_id}`}
                        >
                          {busy === r.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />}
                          Force logout
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSecurityPage;
